import { useCallback } from 'react';

import {
  presignPartRequest,
  retryWithBackoff,
  uploadPartViaWorker,
  uploadWithSlidingWindow,
} from 'api';
import { useTypedDispatch } from 'hooks/useTypedDispatch';
import {
  abortVideoUpload,
  deleteFile,
  endDownloadVideoFile,
  fetchCertainInspection,
  startDownloadVideoFile,
} from 'state';
import { checkIsGoodStatus } from 'utils';

// ── Switch for the S3 multipart-abort call ───────────────────────────────────
// Backend `POST /inspection-file/multipart/video/abort` is live (best-effort +
// idempotent). It aborts the S3 multipart AND hard-deletes the inspection_file
// row, so when enabled it fully replaces the `deleteFile` cleanup call below.
// When `false`, cleanup falls back to `deleteFile` (row removed, S3 multipart
// left for the backend 24h reaper).
export const ABORT_MULTIPART_ENABLED = true;

const CHUNK_SIZE = 1024 * 1024 * 16; // 16MB
const CONCURRENT_PARTS = 10;

export type VideoUploadOutcome =
  | { status: 'success'; file: any; inspectionFileId: string }
  | {
      status: 'failed';
      inspectionFileId?: string;
      key?: string;
      uploadId?: string;
      error?: unknown;
    }
  | {
      status: 'aborted';
      inspectionFileId?: string;
      key?: string;
      uploadId?: string;
    }
  | { status: 'start-failed' };

type RunVideoUploadArgs = {
  file: File;
  inspectionId?: string;
  signal: AbortSignal;
  onProgress: (percent: number) => void;
};

type CleanupArgs = {
  inspectionFileId?: string;
  inspectionId?: string;
  key?: string;
  uploadId?: string;
};

/**
 * Shared multipart-video upload state machine used by both AddInspection and
 * InspectionDetails so the interrupted-upload handling can't drift between them.
 *
 * Guarantees:
 *  - The caller's queue should advance ONLY on `status: 'success'`.
 *  - On any failure/abort the S3 multipart upload is aborted (when enabled) and
 *    the inspection_file row is deleted, awaited/confirmed, before returning.
 *  - The final `/complete` call is retried with backoff so a transient failure
 *    doesn't strand a fully-uploaded file.
 */
export const useVideoUpload = () => {
  const dispatch = useTypedDispatch();

  const cleanupMultipart = useCallback(
    async ({
      inspectionFileId,
      inspectionId,
      key,
      uploadId,
    }: CleanupArgs): Promise<boolean> => {
      let removed = false;

      // Preferred path: the backend abort endpoint aborts the S3 multipart AND
      // hard-deletes the inspection_file row in one call, so it fully replaces
      // deleteFile. It's best-effort/idempotent (aborting a gone/expired upload
      // still returns success). `key` is required; `uploadId` is sent when known.
      if (ABORT_MULTIPART_ENABLED && key) {
        try {
          const res = await dispatch(abortVideoUpload({ key, uploadId }));

          removed = Boolean(checkIsGoodStatus(res?.status));
        } catch (err) {
          console.error('[Upload] multipart abort failed', err);
        }
      }

      // Fallback only: abort was skipped/unavailable/failed. Remove the row so
      // we don't leave a stuck record (S3 multipart is reaped server-side).
      // Never runs alongside a successful abort — that would double-delete.
      if (!removed && inspectionFileId) {
        const res = await dispatch(deleteFile(inspectionFileId));

        removed = Boolean(checkIsGoodStatus(res?.status));
      }

      if (removed && inspectionId) {
        await dispatch(fetchCertainInspection(inspectionId));
      }

      return removed;
    },
    [dispatch],
  );

  const runVideoUpload = useCallback(
    async ({
      file,
      inspectionId,
      signal,
      onProgress,
    }: RunVideoUploadArgs): Promise<VideoUploadOutcome> => {
      const startRes = await dispatch(
        startDownloadVideoFile({
          fileName: file.name,
          type: 'mp4',
          inspectionId: inspectionId as string,
        }),
      );

      if (!checkIsGoodStatus(startRes?.status)) {
        return { status: 'start-failed' };
      }

      const key = startRes.data.uploadProcess.Key;
      const uploadId = startRes.data.uploadProcess.UploadId;
      const inspectionFileId = startRes.data.inspectionFile.id;
      const fileInspectionId = startRes.data.inspectionFile.inspectionId;

      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      let uploadFailed = false;
      let uploadResults: { ETag: string; PartNumber: number }[] = [];

      try {
        uploadResults = await uploadWithSlidingWindow(
          totalChunks,
          CONCURRENT_PARTS,
          async partNumber => {
            const chunkStart = (partNumber - 1) * CHUNK_SIZE;
            const chunk = file.slice(chunkStart, chunkStart + CHUNK_SIZE);
            const presignRes = await retryWithBackoff(
              async () => {
                const res = await presignPartRequest(
                  { key, uploadId, partNumber },
                  signal,
                );

                if (!res?.data?.url) throw new Error('Presign request failed');

                return res;
              },
              signal,
              6,
              1000,
              15_000,
            );

            return uploadPartViaWorker(presignRes.data.url, chunk, signal);
          },
          (completed, total) => {
            onProgress(Math.min(Math.round((completed / total) * 100), 99));
          },
          signal,
        );
      } catch (err) {
        if (!signal.aborted) {
          uploadFailed = true;
          console.error('[Upload] Part upload failed:', err);
        }
      }

      if (signal.aborted) {
        await cleanupMultipart({
          inspectionFileId,
          inspectionId: fileInspectionId,
          key,
          uploadId,
        });

        return { status: 'aborted', inspectionFileId, key, uploadId };
      }

      if (uploadFailed) {
        await cleanupMultipart({
          inspectionFileId,
          inspectionId: fileInspectionId,
          key,
          uploadId,
        });

        return { status: 'failed', inspectionFileId, key, uploadId };
      }

      // Complete the upload — retried so a transient failure of the final call
      // doesn't strand a fully-uploaded file.
      let endRes: any;

      try {
        endRes = await retryWithBackoff(
          async () => {
            const res = await dispatch(
              endDownloadVideoFile({ key, uploadId, uploadResults }),
            );

            if (!checkIsGoodStatus(res?.status)) {
              throw new Error('Complete request failed');
            }

            return res;
          },
          signal,
          4,
          1000,
          15_000,
        );
      } catch (err) {
        await cleanupMultipart({
          inspectionFileId,
          inspectionId: fileInspectionId,
          key,
          uploadId,
        });

        return { status: 'failed', inspectionFileId, key, uploadId, error: err };
      }

      onProgress(100);

      return {
        status: 'success',
        file: endRes?.data?.file,
        inspectionFileId,
      };
    },
    [dispatch, cleanupMultipart],
  );

  return { runVideoUpload, cleanupMultipart };
};
